import { ISelectOption } from '@/components/form/fields/SelectField';
import { axiosInstance } from '@/utils/libs/axios';
import _ from 'lodash';
import { TreeNode } from 'primereact/treenode';
import { useMutation, useQuery } from '@tanstack/react-query';
import { z } from 'zod';
import { IModuleSchema } from './module';
import { IShareTestIssueSchema } from './testIssueShare';
import { makeModuleOptions } from '@/utils/libs/moduleUtils';
import { IUserSchema } from './user';
import { IReleaseSchema } from './release';
import { fileSchemaOptional } from '@/utils/libs/schemaUtil';
import { ITestIssueLogSchema } from './testIssueLog';
import { IDetailTestRunSchema } from './testRunDetail';
import { ICommentSchema } from './comment';

export const testIssueSchema = z.object({
  id: z.string().optional(),
  title: z.string({ required_error: 'Title is required' }).min(1, 'Title is required'),
  description: z.string({ required_error: 'Description is required' }).min(1, 'Description is required'),
  category: z.string({ required_error: 'Category is required' }).min(1, 'Category is required'),
  module_id: z.string().optional().nullable(),
  test_run_id: z.string().optional().nullable(),
  release_id: z.string({ required_error: 'Release is required' }).min(1, 'Release is required'),
  status: z.string({ required_error: 'Status is required' }).min(1, 'Status is required'),
  issue_type: z.string({ required_error: 'Issue Type is required' }).min(1, 'Issue Type is required'),
  severity: z.string({ required_error: 'Severity is required' }).min(1, 'Severity is required'),
  priority: z.string({ required_error: 'Priority is required' }).min(1, 'Priority is required'),
  assignee_issue: z.string({ required_error: 'Assignee is required' }).min(1, 'Assignee is required'),
  is_archive: z.boolean().optional(),
  project_id: z.string().optional(),
  environment_issue: z.string().optional(),
  step_issue: z.string().optional().nullable(),
  code_issue: z.string().optional().nullable(),
  medias: fileSchemaOptional,
});

export const importFileSchema = z.object({
  file: z.any().refine(file => !_.isEmpty(file), 'File is Required'),
});

export type ITestIssueSchema = z.infer<typeof testIssueSchema> & {
  module?: IModuleSchema;
  assignee?: IUserSchema;
  release?: IReleaseSchema;
  created_by?: IUserSchema;
  test_run?: { id: string; title: string };
  comments?: ICommentSchema[];
  created_at?: string;
};

export type ITestIssueOptions = {
  status: ISelectOption[];
  priority: ISelectOption[];
  category: ISelectOption[];
  severity: ISelectOption[];
  environment: ISelectOption[];
  issue_type: ISelectOption[];
  assignee_issue: ISelectOption[];
};

export type IDropDown = {
  category: ISelectOption[];
  status: ISelectOption[];
  priority: ISelectOption[];
  issue_type: ISelectOption[];
  assignee_issue: ISelectOption[];
  module: TreeNode[];
  severity: ISelectOption[];
  release: ISelectOption[];
  test_run: ISelectOption[];
  environment: ISelectOption[];
};

interface IUserItem {
  id: string;
  full_name: string;
}

interface ITestRunItem {
  id: string;
  title: string;
}

export type IImportFileSchema = z.infer<typeof importFileSchema>;

export const TestIssueService = {
  useCreate: () =>
    useMutation({
      mutationFn: async ({
        data,
        testRunDetail,
      }: {
        data: ITestIssueSchema;
        testRunDetail?: IDetailTestRunSchema;
      }) => {
        const response = await axiosInstance.post(
          '/api/crud/test-issue',
          data,
          { headers: { 'Content-Type': 'multipart/form-data' } },
        );

        // assign test issue id to test run detail issue
        if (testRunDetail) {
          const testIssue: ITestIssueSchema = response.data.data;
          await axiosInstance.post(
            '/api/crud/test-run-detail-issue',
            {
              test_issue_id: testIssue.id,
              test_run_detail_id: testRunDetail.id,
              is_create: true,
            },
            { headers: { 'Content-Type': 'application/json' } },
          );
        }
      },
    }),

  useGet: (id?: string) =>
    useQuery<ITestIssueSchema>({
      queryKey: ['test-issue', id],
      queryFn: async () => {
        if (!id) return {};

        const response = await axiosInstance.get(`/api/crud/test-issue/${id}`);
        return response.data?.data;
      },
    }),

  useUpdate: () =>
    useMutation({
      mutationFn: async (data: ITestIssueSchema) => {
        const response = await axiosInstance.post(
          `/api/crud/test-issue/${data.id}`,
          data,
          { headers: { 'Content-Type': 'multipart/form-data' } },
        );
        return response.data;
      },
    }),

  useDelete: () =>
    useMutation({
      mutationFn: async (id: string) => {
        const response = await axiosInstance.delete(
          `/api/crud/test-issue/${id}`,
        );
        return response.data;
      },
    }),

  useExport: () =>
    useMutation({
      mutationFn: async ({ id, filters, sort, orderBy }: { id: string; filters?: { [x: string]: any }; sort?: string; orderBy?: 'asc' | 'desc' }) => {
        const filterParam = Object.fromEntries(Object.entries(filters ?? {}).map(([key, value]) => [`filter[${key}]`, value]));

        const response = await axiosInstance.get(
          `/api/crud/test-issue/${id}/export`,
          {
            params: { ...filterParam, sort, asc: orderBy == 'asc' },
            responseType: 'blob',
          },
        );

        return response.data;
      },
    }),

  useImport: (projectId: string) =>
    useMutation({
      mutationFn: async (data: IImportFileSchema) => {
        const response = await axiosInstance.post(
          `/api/crud/test-issue/${projectId}/import`,
          data,
          { headers: { 'Content-Type': 'multipart/form-data' } },
        );
        return response.data;
      },
    }),

  useGetDropDown: (projectId?: string) =>
    useQuery<IDropDown>({
      queryKey: ['test-issue-dropdown'],
      queryFn: async () => {
        // options
        const optionsResponse = await axiosInstance.get(
          '/api/crud/test-issue/options',
        );

        // user options
        const userResponse = await axiosInstance.get(
          '/api/crud/project-assignee/all',
          {
            params: {
              filter: { project_id: projectId },
            },
          },
        );

        const userOptions = userResponse.data.all.filter((item: IUserItem) => item.full_name?.trim() && item.id).map((item: IUserItem) => ({
          value: item.id,
          label: item.full_name,
        }));

        // release options
        const releaseResponse = await axiosInstance.get<IReleaseSchema[]>(
          '/api/crud/release/all',
          { params: { filter: { project_id: projectId } } },
        );

        const releaseOptions: ISelectOption[] = releaseResponse.data
          .filter((item: IReleaseSchema) => item.id !== undefined)
          .map((item: IReleaseSchema) => ({ value: item.id!, label: item.name }));
        
        const testRunResponse = await axiosInstance.get('/api/crud/test-run', {
          params: {
            filter: { project_id: projectId, is_archive: 'false' },
            show_all: true,
          },
        });

        const rawTestRunData: ITestRunItem[] = Array.isArray(
          testRunResponse.data?.data?.content,
        )
          ? testRunResponse.data.data.content
          : Array.isArray(testRunResponse.data?.data)
            ? testRunResponse.data.data
            : Array.isArray(testRunResponse.data)
              ? testRunResponse.data
              : [];

        const testRunOptions: ISelectOption[] = rawTestRunData
          .filter((item: ITestRunItem) => item && item.id !== undefined)
          .map((item: ITestRunItem) => ({
            value: item.id,
            label: item.title,
          }));
        // module options
        const moduleResponse = await axiosInstance.get<IModuleSchema[]>(
          '/api/crud/module/all',
          { params: { filter: { project_id: projectId } } },
        );

        const moduleOptions = makeModuleOptions(moduleResponse.data);

        return {
          priority: optionsResponse.data?.data?.priority ?? [],
          assignee_issue: userOptions,
          release: releaseOptions ?? [],
          test_run: testRunOptions ?? [],
          module: moduleOptions,
          category: optionsResponse.data?.data?.category ?? [],
          status: optionsResponse.data?.data?.status ?? [],
          issue_type: optionsResponse.data?.data?.type ?? [],
          severity: optionsResponse.data?.data?.severity ?? [],
          environment: optionsResponse.data?.data?.environment ?? [],
        };
      },
    }),

  useGetShare: (id?: string) =>
    useQuery<IShareTestIssueSchema>({
      queryKey: ['test-issue-shared', id],
      queryFn: async () => {
        if (!id) return {};

        const response = await axiosInstance.get(
          `/api/crud/test-issue/${id}/share`,
        );

        return response.data?.data;
      },
    }),

  useGetByTestRunDetailId: (testRunDetailId?: string) =>
    useQuery<ITestIssueSchema>({
      queryKey: ['test-issue-by-test-run-detail'],
      queryFn: async () => {
        if (!testRunDetailId) return {};

        const { data } = await axiosInstance.get(`/api/crud/test-issue`, {
          params: { filter: { test_run_detail_id: testRunDetailId } },
        });

        const content = data.data?.content;
        return content?.[0] ?? {};
      },
    }),

  useGetOptions: (projectId: string) =>
    useQuery<ITestIssueOptions>({
      queryKey: ['test-issue-options', projectId],
      queryFn: async () => {
        const { data: optionsData } = await axiosInstance.get(
          '/api/crud/test-issue/options',
        );

        const userResponse = await axiosInstance.get(
          '/api/crud/project-assignee/all',
          {
            params: {
              filter: { project_id: projectId },
            },
          },
        );

        const userOptions = userResponse.data.all.filter((item: IUserItem) => item.full_name?.trim() && item.id).map((item: IUserItem) => ({
          value: item.id,
          label: item.full_name,
        }));

        return {
          status: optionsData.data.status,
          priority: optionsData.data.priority,
          category: optionsData.data.category,
          severity: optionsData.data.severity,
          environment: optionsData.data.environment,
          issue_type: optionsData.data.type,
          assignee_issue: userOptions,
        };
      },
    }),
  useArchive: () =>
    useMutation({
      mutationFn: async (id: string) => {
        const response = await axiosInstance.post(
          `/api/crud/test-issue/${id}/archive`,
        );

        return response.data;
      },
    }),

  useUnarchive: () =>
    useMutation({
      mutationFn: async (id: string) => {
        const response = await axiosInstance.post(
          `/api/crud/test-issue/${id}/unarchive`,
        );

        return response.data;
      },
    }),

  useUnlink: () =>
    useMutation({
      mutationFn: async (id: string) => {
        const response = await axiosInstance.put(
          `/api/crud/test-run-detail-issue/${id}/unlink`,
        );

        return response.data;
      },
    }),

  useGetStatusLogs: (id: string) => {
    return useQuery<ITestIssueLogSchema[]>({
      queryKey: ['test-issue-status-logs', id],
      queryFn: async () => {
        const response = await axiosInstance.get(
          `/api/crud/test-issue/${id}/logs`,
        );
        return response.data.logs;
      },
    });
  },
};
